import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "./DashboardLayout";
import { api, formatDateMX, MXN } from "../../lib/apiClient";
import { statusMeta } from "../../lib/constants";
import { ClipboardList, CheckCircle2, CalendarClock, CreditCard, Users, TrendingUp, PartyPopper, ArrowRight } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="relative w-full min-w-0 overflow-hidden bg-blancoCalido border border-beige rounded-[22px] p-3 shadow-pastel" data-testid={`stat-${label}`}>
    <div className="absolute -right-5 -top-6 w-16 h-16 rounded-full opacity-20" style={{ background: color }} />
    <div className="relative flex items-start justify-between gap-2 min-w-0">
      <div className="w-9 h-9 rounded-full flex shrink-0 items-center justify-center" style={{ background: `${color}22`, color }}><Icon className="w-4 h-4" /></div>
      <span className="font-serif text-3xl text-vino leading-none tabular-nums truncate">{value}</span>
    </div>
    <p className="relative text-[11px] min-[380px]:text-xs text-cafe/60 mt-3 leading-tight">{label}</p>
  </div>
);

export default function Resumen() {
  const [s, setS] = useState(null);
  const navigate = useNavigate();
  const load = React.useCallback(() => api.get("/summary").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  useAdminRealtime(load, ["orders", "events"]);

  return (
    <DashboardLayout title="Resumen">
      {!s ? <p className="text-cafe/50">Cargando…</p> : (
        <div className="w-full min-w-0 space-y-5 sm:space-y-6">
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4">
            <Stat icon={ClipboardList} label="Pedidos nuevos" value={s.nuevos} color="#C8943F" />
            <Stat icon={CheckCircle2} label="Confirmados" value={s.confirmados} color="#9BA77D" />
            <Stat icon={CalendarClock} label="Para hoy" value={s.hoy} color="#761B38" />
            <Stat icon={CreditCard} label="Pagos pendientes" value={s.pagos_pendientes} color="#DCA9B5" />
            <Stat icon={Users} label="Clientes" value={s.total_clientes} color="#4B1025" />
            <Stat icon={TrendingUp} label="Pedidos (semana)" value={s.semana} color="#C8943F" />
            <Stat icon={PartyPopper} label="Eventos nuevos" value={s.eventos_nuevos} color="#9BA77D" />
          </div>

          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <button onClick={() => navigate("/admin/pedidos")} className="w-full sm:w-auto justify-center bg-vino text-crema rounded-full px-6 py-3 text-sm hover:bg-vinoDark flex items-center gap-2 min-w-0"><ClipboardList className="w-4 h-4 shrink-0" /> <span>Ver pedidos</span></button>
            <button onClick={() => navigate("/admin/disponibilidad")} className="w-full sm:w-auto justify-center border-2 border-vino text-vino rounded-full px-6 py-3 text-sm hover:bg-vino/5 flex items-center gap-2 min-w-0"><span>Bloquear fecha</span></button>
          </div>

          <div className="w-full min-w-0 bg-blancoCalido border border-beige rounded-[24px] p-4 sm:p-6 shadow-pastel">
            <h3 className="font-serif text-xl text-vino mb-4">Próximas entregas</h3>
            {s.proximas_entregas.length === 0 ? <p className="text-cafe/50 text-sm">Sin entregas próximas.</p> : (
              <div className="space-y-2">
                {s.proximas_entregas.map((o) => {
                  const m = statusMeta(o.status);
                  return (
                    <button key={o.id} onClick={() => navigate(`/admin/pedidos?open=${o.id}`)} className="w-full min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-2xl hover:bg-beige/50 text-left">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-cafe">{o.customer_name} · <span className="text-cafe/60">{o.product_name}</span></p>
                        <p className="text-xs text-cafe/50">{formatDateMX(o.delivery_date)} · {o.delivery_time || "-"}</p>
                      </div>
                      <div className="flex items-center gap-2 self-start sm:self-auto">
                        <span className="text-xs px-2.5 py-1 rounded-full text-white" style={{ background: m.color }}>{m.label}</span>
                        <ArrowRight className="w-4 h-4 text-cafe/40" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
